import { OrganizationUserType } from '@mfw/common';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { useIntl } from 'react-intl';

import { useOrganizationContext, useAuthContext } from '../../context';
import { resolveClientMembershipAlert } from './clientMembershipAlert';
import { useOrgMembership } from '../../context/OrganizationProvider/useOrgMembership';
import { useClientMembershipRows } from '../../hooks/memberships';
import { useNavigate } from '../../router/hooks';
import messages from './messages';

/**
 * Tells a client, on every page, that their own membership in the selected
 * organization is unpaid or has run out — until now they only found out when
 * the trainer reminded them or they opened Profile → Membership themselves.
 *
 * Only for the CLIENT role of THAT organization (role is a property of the
 * account/organization pair, so `currentUser.type` is not read here). Never for
 * a platform user, who is no member of any organization and pays nothing, nor
 * in the org-less personal profile.
 */
export const ClientMembershipAlertBanner = () => {
  const { formatMessage } = useIntl();
  const { currentUser } = useAuthContext();
  const { currentOrganization } = useOrganizationContext();
  const orgMembership = useOrgMembership();
  const navigate = useNavigate();

  const organizationId = currentOrganization?.id ?? '';
  const clientId = currentUser?.id ?? '';
  const isClientHere =
    !currentUser?.isPlatformUser &&
    orgMembership?.type === OrganizationUserType.CLIENT;

  // Mounted once for the whole session, so nothing else re-reads it: a payment
  // the desk records, or a cycle running out, reaches the client when they
  // come back to the app (still bounded by the 2-minute staleTime).
  const { data: rows } = useClientMembershipRows(
    {
      clientId: isClientHere ? clientId : '',
      organizationId: isClientHere ? organizationId : '',
    },
    { refetchOnWindowFocus: true },
  );

  if (!isClientHere || !rows) return null;
  const alert = resolveClientMembershipAlert(rows);
  if (!alert) return null;

  return (
    <Alert
      action={
        <Button
          // `me/:userId`, the client's own "My profile" — not the staff
          // `clients/:userId`, which adds a back-link to the clients list
          onClick={() =>
            navigate('/organizations/:organizationId/me/:userId', {
              params: { organizationId, userId: clientId },
              search: 'tab=memberships',
            })
          }
          data-testid="client-membership-alert-view"
          color="inherit"
          size="small"
        >
          {formatMessage(messages.clientMembershipAlertView)}
        </Button>
      }
      data-testid={`client-membership-alert-${alert}`}
      severity={alert === 'unpaid' ? 'error' : 'warning'}
      square
    >
      {formatMessage(
        alert === 'unpaid'
          ? messages.clientMembershipAlertUnpaid
          : messages.clientMembershipAlertExpired,
        { organization: currentOrganization?.name ?? '' },
      )}
    </Alert>
  );
};
